import ExpandedChip from '@/components/Chips/ExpandedChip';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import WebsiteLink from '@/images/WebsiteLink.svg';
import styled from '@emotion/styled';
import { useMemo, useState } from 'react';
import { NotSure, OnboardingClass, Other } from '../../steps';
import { ActionButton, BackAndForthSection, BackButton, ChipContainer, Content, HorizontalSection, Section, Title } from './Styled';

const Multiple = styled.div<{ show?: boolean }>`
    display: ${({ show }) => (!show ? 'none' : 'block')};
    color: ${({ theme }) => theme.colors.secondary[600]};
    text-align: center;
    font-size: 20px;
    font-weight: 400;
    line-height: 125%;
    letter-spacing: -0.4px;
`;

type TextboxProps = {
    placeholder?: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    value: string;
    name: string;
    label?: string;
    icon?: string;
    isError?: boolean;
};

const Textbox: React.FC<TextboxProps> = ({ placeholder, onChange, value, name, label, icon, isError }) => {
    return (
        <div style={{ marginBlockStart: '25px' }}>
            {label && (
                <label htmlFor={name} style={{ display: 'block', marginBottom: '8px' }}>
                    {label}
                </label>
            )}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: '#f5f5f5',
                    padding: '8px',
                    borderRadius: '12px',
                    border: `2px solid ${isError ? 'red' : '#ccc'}`,
                    maxWidth: '1000px',
                    gap: '8px',
                }}>
                {icon && <MaskedIcon src={icon} size="24px" style={{ marginLeft: '8px' }} />}
                <input
                    type="text"
                    id={name}
                    name={name}
                    placeholder={placeholder}
                    value={value}
                    onChange={onChange}
                    style={{
                        fontSize: '18px',
                        textAlign: 'left',
                        border: 'none',
                        outline: 'none',
                        flex: 1,
                        backgroundColor: 'transparent',
                        width: '750px',
                        height: '55px',
                    }}
                />
            </div>
            {isError && (
                <span
                    style={{
                        color: 'red',
                        marginTop: '8px',
                        display: 'block',
                        fontSize: '18px',
                        textAlign: 'left',
                    }}>
                    Please enter a valid URL
                </span>
            )}
        </div>
    );
};

export interface StepOption {
    id?: string;
    label: string | OnboardingClass;
    explanation?: string;
    icon?: string;
}
export interface Step {
    id: string | OnboardingClass;
    title: string;
    multiSelect: boolean;
    randomize: boolean;
    showCondition?: {
        stepId: string;
        selectedOption: string;
    };
    options?: StepOption[];
    skippable?: boolean;
    textInput?: boolean;
    shouldNestWithOtherSuffix?: boolean;
}

interface Option {
    value: {
        id?: string;
        label: string;
        explanation?: string;
    };
    onClick: VoidFunction;
    checked: boolean;
}

const MAX_ANSWERS_NUMBER = 20;
const seed = Array.from({ length: MAX_ANSWERS_NUMBER }, () => Math.random() - 0.5);

interface Props {
    show: boolean;
    last: boolean;
    options: Option[];
    onNext?: VoidFunction;
    onBack?: VoidFunction;
    setCustomAnswer?: (value: string) => void;
    state: Record<string, string>;
    step: any;
}

function isValidURL(text: string) {
    if (text.trim() === '') return true;
    const urlPattern = /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)(:\d+)?(\/[^\s]*)?$/i;
    return urlPattern.test(text);
}

export default function NewStep({ show, options, onNext, onBack, setCustomAnswer, last, step, state }: Props) {
    const randomized = useMemo(() => {
        if (!step.randomize) {
            return options;
        }

        const other = options.find(option => option.value.label === Other);
        const notSure = options.find(option => option.value.label === NotSure);

        const noOtherOrSureRandomized = options
            .filter(option => option.value.label !== Other && option.value.label !== NotSure)
            .map((option, index) => ({ option, index }))
            .sort((a, b) => seed[a.index] - seed[b.index])
            .map(option => option.option);

        const randomized = [...noOtherOrSureRandomized, ...(notSure ? [notSure] : []), ...(other ? [other] : [])];
        return randomized;
    }, [options, step.randomize]);
    const [text, setText] = useState('');
    const [isTextError, setIsTextError] = useState(false);

    return (
        <Content show={show}>
            <HorizontalSection hasBack={false}>
                <Title>{step.title}</Title>
            </HorizontalSection>
            <Section>
                <Multiple show={step.multiSelect}>You can choose more than one</Multiple>
                {step.textInput ? (
                    <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '16px' }}>
                        <Textbox
                            placeholder="https://www.d-id.com/"
                            value={text}
                            onChange={e => {
                                setText(e.target.value);
                                setCustomAnswer!(e.target.value);
                                setIsTextError(!isValidURL(e.target.value));
                            }}
                            name="websiteTextbox"
                            icon={WebsiteLink}
                            isError={isTextError}
                        />
                    </div>
                ) : (
                    <ChipContainer>
                        {randomized.map((option, idx) => (
                            <ExpandedChip
                                key={idx}
                                value={option.value}
                                onClick={() => {
                                    option.onClick();
                                }}
                                isMultiple={step.multiSelect}
                                checked={option.checked}
                                isText={option.value.label === Other}
                                setText={setCustomAnswer}
                            />
                        ))}
                    </ChipContainer>
                )}
                <BackAndForthSection>
                    <BackButton fullWidth color="info" variant="contained" onClick={onBack} disabled={false}>
                        {'Back'}
                    </BackButton>
                    <ActionButton
                        fullWidth
                        color="secondary"
                        variant="contained"
                        onClick={onNext}
                        disabled={!step.skippable && !state[step.id]?.length}>
                        {last ? '🎉 Done' : 'Next'}
                    </ActionButton>
                </BackAndForthSection>
            </Section>
        </Content>
    );
}
